import React from 'react';
import { Paper, Box, Typography, Skeleton } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { formatDate, formatCurrency, getCurrencySymbol } from '../../utils/dashboardHelpers';

/**
 * Gráfico de evolución de ingresos
 * Muestra un área chart con la evolución temporal de ingresos
 */
const EvolucionIngresosChart = ({ data, loading = false }) => {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400
        }}
      >
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="text.secondary">No hay datos disponibles</Typography>
      </Paper>
    );
  }

  // Formatear datos para el gráfico
  const chartData = data.map(item => ({
    fecha: formatDate(item.fecha, 'dd MMM'),
    ingresos: parseFloat(item.ingresos_totales) || 0,
    comisiones: parseFloat(item.comisiones_totales) || 0
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color: '#4CAF50', fontSize: '0.8rem' }}>
            Ingresos: <strong>{formatCurrency(payload[0]?.value || 0)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: '#FF9800', fontSize: '0.8rem' }}>
            Comisiones: <strong>{formatCurrency(payload[1]?.value || 0)}</strong>
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        height: 400
      }}
    >
      {/* Header */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#1A237E',
          fontSize: '1.125rem',
          mb: 2
        }}
      >
        💰 Evolución de Ingresos
      </Typography>

      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
        >
          <defs>
            <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="colorComisiones" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FF9800" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#FF9800" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="fecha"
            stroke="#666"
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis
            stroke="#666"
            style={{ fontSize: '0.75rem' }}
            tickFormatter={(value) => `${getCurrencySymbol()} ${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="ingresos"
            stroke="#4CAF50"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorIngresos)"
            name="Ingresos"
          />
          <Area
            type="monotone"
            dataKey="comisiones"
            stroke="#FF9800"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorComisiones)"
            name="Comisiones"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default EvolucionIngresosChart;
